import moment, { Moment } from 'moment/moment'
import { filter, IPayload } from '@/core/utils/payload-helper'
import { Stroke, StrokePayload } from '@/core/models/stroke'
import { Role, RolePayload } from '@/core/models/role'
import { Goal, GoalPayload } from '@/core/models/goal'
import { TrainingProgram, TrainingProgramPayload } from '@/core/models/training-program'
import { WorkoutExecution, WorkoutExecutionPayload } from '@/core/models/workout-execution'

export interface ILocationCoordinates {
    latitude: number
    longitude: number
}

export interface UserPayload {
    id: number
    stroke_id: number
    language_id: string
    first_name: string
    last_name: string
    email: string
    email_verified_at?: string
    phone: string
    password?: string
    password_confirmation?: string
    gender: string
    birth_date: string
    photo_url: string
    city: string
    country_code_iso: string
    location_coordinates: ILocationCoordinates
    os: string
    user_agent: string
    weight: number
    weight_unit: string
    height: number
    height_unit: string
    training_count_week: number
    swimming_goal_meters: number
    swimming_locations?: string[]
    level: string
    css_value: string
    pace_value: number
    pace: string
    distance_unit: string
    color_mode: string
    registered_at: string
    apple: any[]
    garmin: any[]
    strava: any[]
    suunto: any[]
    coros: any[]
    huawei: any[]
    polar: any[]
    ffn: boolean
    allow_face_id: boolean
    allow_notifications: boolean
    accept_terms: boolean
    accept_confidentiality: boolean
    premium_status: string
    status: string
    preferred_locale: string
    provider: string
    permissions?: string[]
    roles?: RolePayload[]
    goals: number[]
    strokes: number[]
    workout_executions?: WorkoutExecutionPayload[]
    user_training_program?: TrainingProgramPayload
    workout_executions_count?: number
}

export class User {

    id: number
    strokeId: number
    languageId: string
    firstName: string
    lastName: string
    email: string
    emailVerifiedAt?: Moment
    phone: string
    password?: string
    passwordConfirmation?: string
    gender: string
    birthDate: string
    photoUrl: string
    city: string
    countryCodeIso: string
    locationCoordinates: ILocationCoordinates
    os: string
    userAgent: string
    weight: number | null
    weightUnit: string
    height: number | null
    heightUnit: string
    trainingCountWeek: number
    swimmingGoalMeters: number
    swimmingLocations: string[]
    level: string
    cssValue: string
    paceValue: number
    pace: string
    distanceUnit: string
    colorMode: string
    registeredAt: Moment
    apple: any[]
    garmin: any[]
    strava: any[]
    suunto: any[]
    coros: any[]
    huawei: any[]
    polar: any[]
    ffn: boolean
    allowFaceId: boolean
    allowNotifications: boolean
    acceptTerms: boolean
    acceptConfidentiality: boolean
    premiumStatus: string
    status: string
    preferredLocale: string
    provider: string
    permissions: string[]
    roles: Role[] = <Role[]>[]
    roleIds: number[] = []
    goals: number[] = []
    strokes: number[] = []
    workoutExecutions: WorkoutExecution[] = <WorkoutExecution[]>[]
    workoutExecutionsCount: number
    program?: TrainingProgram

    constructor(data: UserPayload) {
        this.id = data.id
        this.strokeId = data.stroke_id
        this.languageId = data.language_id
        this.firstName = data.first_name
        this.lastName = data.last_name || ''
        this.email = data.email
        this.emailVerifiedAt = data.email_verified_at ? moment(data.email_verified_at) : undefined
        this.phone = data.phone
        this.password = data.password
        this.gender = data.gender
        this.birthDate = data.birth_date
        this.photoUrl = data.photo_url
        this.city = data.city
        this.countryCodeIso = data.country_code_iso
        this.locationCoordinates = data.location_coordinates
        this.os = data.os
        this.userAgent = data.user_agent
        this.weight = data.weight ? data.weight : null
        this.weightUnit = data.weight_unit
        this.height = data.height ? data.height : null
        this.heightUnit = data.height_unit
        this.trainingCountWeek = data.training_count_week
        this.swimmingGoalMeters = data.swimming_goal_meters
        this.swimmingLocations = data.swimming_locations || []
        this.level = data.level
        this.cssValue = data.css_value
        this.paceValue = data.pace_value
        this.pace = data.pace
        this.distanceUnit = data.distance_unit
        this.colorMode = data.color_mode
        this.registeredAt = moment(data.registered_at)
        this.apple = data.apple
        this.garmin = data.garmin
        this.strava = data.strava
        this.suunto = data.suunto
        this.coros = data.coros
        this.huawei = data.huawei
        this.polar = data.polar
        this.ffn = data.ffn
        this.allowFaceId = data.allow_face_id
        this.allowNotifications = data.allow_notifications
        this.acceptTerms = data.accept_terms
        this.acceptConfidentiality = data.accept_confidentiality
        this.premiumStatus = data.premium_status
        this.status = data.status
        this.preferredLocale = data.preferred_locale
        this.provider = data.provider
        this.permissions = data.permissions || []
        this.workoutExecutionsCount = data.workout_executions_count || 0
        this.capture(data)
    }

    fill(data: UserPayload): this {
        this.id = data.id
        this.strokeId = data.stroke_id
        this.languageId = data.language_id
        this.firstName = data.first_name
        this.lastName = data.last_name || ''
        this.email = data.email
        this.emailVerifiedAt = data.email_verified_at ? moment(data.email_verified_at) : undefined
        this.phone = data.phone
        this.password = data.password
        this.gender = data.gender
        this.birthDate = data.birth_date
        this.photoUrl = data.photo_url
        this.city = data.city
        this.countryCodeIso = data.country_code_iso
        this.locationCoordinates = data.location_coordinates
        this.os = data.os
        this.userAgent = data.user_agent
        this.weight = data.weight ? data.weight : null
        this.weightUnit = data.weight_unit
        this.height = data.height ? data.height : null
        this.heightUnit = data.height_unit
        this.trainingCountWeek = data.training_count_week
        this.swimmingGoalMeters = data.swimming_goal_meters
        this.swimmingLocations = data.swimming_locations || []
        this.level = data.level
        this.cssValue = data.css_value
        this.paceValue = data.pace_value
        this.pace = data.pace
        this.distanceUnit = data.distance_unit
        this.colorMode = data.color_mode
        this.registeredAt = moment(data.registered_at)
        this.apple = data.apple
        this.garmin = data.garmin
        this.strava = data.strava
        this.suunto = data.suunto
        this.coros = data.coros
        this.huawei = data.huawei
        this.polar = data.polar
        this.ffn = data.ffn
        this.allowFaceId = data.allow_face_id
        this.allowNotifications = data.allow_notifications
        this.acceptTerms = data.accept_terms
        this.acceptConfidentiality = data.accept_confidentiality
        this.premiumStatus = data.premium_status
        this.status = data.status
        this.preferredLocale = data.preferred_locale
        this.provider = data.provider
        this.permissions = data.permissions || []
        this.workoutExecutionsCount = data.workout_executions_count || 0
        this.capture(data)
        return this
    }

    capture(data: UserPayload): this {
        this.roles = <Role[]>[]
        this.roleIds = <number[]>[]
        data.roles?.map((item) => {
            this.roles.push(new Role(item))
            this.roleIds.push(item.id)
        })

        this.goals = data.goals
        this.strokes = data.strokes

        this.workoutExecutions = <WorkoutExecution[]>[]
        data.workout_executions?.map(item => this.workoutExecutions.push(new WorkoutExecution(item)))

        if (data.user_training_program) {
            this.program = new TrainingProgram(data.user_training_program)
        }
        return this
    }

    public get fullName(): string {
        return this.firstName + (this.lastName ? ' ' + this.lastName : '')
    }

    public get photo(): string {
        return this.photoUrl || '/images/no-avatar.svg'
    }

    public get rolesStrTitle(): string {
        let args = <any>[]
        this.roles.map(item => args.push(item.name))
        return args.join(' / ')
    }

    public get coordinatesTitle(): string {
        if (this.locationCoordinates) {
            return this.locationCoordinates.latitude + ' / ' +  this.locationCoordinates.longitude
        }
        return '-'
    }

    public get suspended(): boolean {
        return this.status === 'suspended'
    }

    public get deleted(): boolean {
        return this.status === 'deleted'
    }

    public get watches(): any[] {
        let watches = <any>[]
        if (this.apple) {
            watches.push('apple')
        }
        if (this.garmin) {
            watches.push('garmin')
        }
        if (this.suunto) {
            watches.push('suunto')
        }
        if (this.coros) {
            watches.push('coros')
        }
        if (this.huawei) {
            watches.push('huawei')
        }
        if (this.polar) {
            watches.push('polar')
        }
        return watches
    }

    public get apps(): any[] {
        let apps = <any>[]
        if (this.strava) {
            apps.push('strava')
        }
        return apps
    }

    permitted(permission: string): boolean {
        return this.permissions.filter(item => item === '*' || item === permission).length > 0
    }

    notPermitted(permission: string): boolean {
        return ! this.permitted(permission)
    }

    exists(): boolean {
        return !! this.id
    }

    unset(): void {
        this.fill(<any>{})
        this.program = undefined
        this.roleIds = <number[]>[]
        this.goals = <number[]>[]
        this.strokes = <number[]>[]
    }

    isNotDeleted(): boolean {
        return this.status !== 'deleted'
    }

    rolesPayload(): RolePayload[] {
        const roles = <any>[]
        this.roleIds.map(id => roles.push(<any>{ id: id }))
        return roles
    }

    payload(fillable: string[] = []): UserPayload | IPayload {
        let payload = {
            id: this.id,
            language_id: this.languageId,
            first_name: this.firstName,
            last_name: this.lastName,
            email: this.email,
            email_verified_at: this.emailVerifiedAt ? this.emailVerifiedAt.format('YYYY-MM-DD HH:mm:ss') : undefined,
            phone: this.phone,
            password: this.password,
            password_confirmation: this.passwordConfirmation,
            gender: this.gender,
            birth_date: this.birthDate,
            photo_url: this.photoUrl,
            city: this.city,
            country_code_iso: this.countryCodeIso,
            location_coordinates: this.locationCoordinates || {},
            weight: Number(this.weight),
            weight_unit: this.weightUnit,
            height: Number(this.height),
            height_unit: this.heightUnit,
            training_count_week: this.trainingCountWeek,
            swimming_goal_meters: this.swimmingGoalMeters,
            swimming_locations: this.swimmingLocations,
            level: this.level,
            css_value: this.cssValue,
            pace_value: this.paceValue,
            pace: this.pace,
            distance_unit: this.distanceUnit,
            color_mode: this.colorMode,
            registered_at: this.registeredAt.format('YYYY-MM-DD HH:mm:ss'),
            allow_face_id: this.allowFaceId,
            allow_notifications: this.allowNotifications,
            accept_terms: this.acceptTerms,
            accept_confidentiality: this.acceptConfidentiality,
            premium_status: this.premiumStatus,
            status: this.status,
            preferred_locale: this.preferredLocale,
            roles: this.rolesPayload(),
            goals: this.goals,
            strokes: this.strokes,
            stroke_id: this.strokeId,
        }
        return filter(payload, fillable)
    }
}
